import type { FC, JSX } from 'react';
import { airportMarkerSelection } from '@/app/components/search/components/SearchPageContainer/MapContainer/utils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { DebugInfoContainer } from '@/app/components/search/components/Banners/Debug/styled.ts';
import DebugBlock from '@/app/components/search/components/Card/Debug/DebugBlock/index.tsx';
import styled, { css } from 'styled-components';
import { adaptTransport } from '@/app/components/hotel-detail/flight-train/TransportDetails/index.tsx';
import { Placeholder, Text } from '@lmn/design-system';
import { calculateCO2EmissionsData, CO2_LEVEL, useDictionary } from '@lmn/lmn-components';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';
import { useIsMobile } from '@/app/components/search/utils/useSsrMedia.ts';
import { SubtleButton } from '@/app/components/hotel-detail/common/button';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { useTranslation } from '@/app/components/translations/Translation';
import { useTransportNotAvailable } from '@/app/components/hotel-detail/flight-train/TransportNotAvailableProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import type { EvoTransportProps } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport/index.tsx';
import EvoTransport from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport/index.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { EvoOvernightBanner } from '../EvoOvernightBanner';
import { TransportAndBrandedFaresContainer, TransportDetailsContainer } from './styled.ts';
import { toUniqueCompanies } from './utils.ts';

/* Must switch with TransportDetailsContainer's column→row breakpoint (768px, see styled.ts).
   Driving this from the JS isMobile flag drew the mobile horizontal rule inside the desktop
   row layout whenever the flag lagged the real width (UA fallback, measured value applied in
   a deferred transition while the page loads), flexing to fill the gap between the halves. */
const Separator = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  width: 100%;
  height: 1px;

  @media (min-width: 768px) {
    width: 1px;
    height: auto;
    padding-bottom: 16px;
  }
`;

const RowSeparator = styled.hr`
  margin: 16px 0 12px;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  width: 100%;
`;

const TransportDetailsSkeleton = styled.div<{ $isOutBound: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;

  ${({ $isOutBound }) =>
    $isOutBound
      ? css`
          margin-bottom: 12px;
          @media (min-width: 768px) {
            margin-bottom: 0px;
            width: 50%;
            padding-right: 24px;
          }
        `
      : css`
          margin-top: 12px;
          @media (min-width: 768px) {
            margin-top: 0px;
            width: 50%;
            padding-left: 24px;
          }
        `}
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
`;

const OvernightBannerWrapper = styled.div`
  &:not(:empty) {
    margin-top: 12px;
  }
`;

const NoTransportAvailableBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundWarningSubtle};
`;

const NoTransportAvailableIcon = styled(IconCircleInfoSolid)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconWarning};
`;

const NoTransportAvailableText = styled(Text)`
  color: ${({ theme }) => theme.kallisto.color.text.static.textWarning};
`;

export const Co2SvgIcon: FC<{ size?: number }> = ({ size = 20 }) => (
  <Icon
    category="Services"
    name="icon-leaf"
    size={Math.round(size * 1.175)}
    footprint={size}
    color="iconPrimary"
    alt=""
  />
);

export type EvoFootprintBadgeProps = {
  footprint: { co2kg: number; averageCo2kg: number };
  locale: string;
};

const FootprintBadgeInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  padding: 0 ${(p) => p.theme.kallisto.space.space100};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundNeutralSubtle};
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  border-radius: ${(p) => p.theme.kallisto.border.radius.radius50};
  white-space: nowrap;
`;

export const EvoFootprintBadge: FC<EvoFootprintBadgeProps> = ({ footprint, locale }) => {
  const dictionary = useDictionary();
  const data = calculateCO2EmissionsData(footprint.co2kg, footprint.averageCo2kg, locale);
  const formattedPercentage = `${data.totalPercentage > 0 ? '+' : ''}${data.totalPercentage}`;
  const badgeLabel =
    data.level === CO2_LEVEL.TYPICAL
      ? dictionary.get('widget.trip_card.footprint.average.short')
      : dictionary.get('widget.trip_card.footprint.badge.percentage', { percentage: formattedPercentage });
  return (
    <FootprintBadgeInner>
      <Co2SvgIcon size={16} />
      <Text variant="BodySmall">{badgeLabel}</Text>
    </FootprintBadgeInner>
  );
};

export type EvoTransportDetailsCardProps = {
  dictionary: Dictionary;
  showDebug: boolean;
  selectedTransport?: TransportsType;
  onTransportDetailsClick?: () => void;
};

const EvoTransportDetailsCard: FC<EvoTransportDetailsCardProps> = ({
  dictionary,
  showDebug,
  selectedTransport,
  onTransportDetailsClick,
}): JSX.Element => {
  const { localization, commonData } = usePageWrapperData();
  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { currentTransportNotAvailable } = useTransportNotAvailable();
  const isWaitingState = !selectedTransport;
  const isMobile = useIsMobile(commonData.isMobileDevice);

  if (isWaitingState) {
    return (
      <TransportAndBrandedFaresContainer>
        <TransportDetailsContainer $isMobile={isMobile}>
          <TransportDetailsSkeleton $isOutBound={true}>
            <Placeholder height="20px" width="116px" />
            <Placeholder height="40px" width="100%" />
          </TransportDetailsSkeleton>
          <Separator />
          <TransportDetailsSkeleton $isOutBound={false}>
            <Placeholder height="20px" width="116px" />
            <Placeholder height="40px" width="100%" />
          </TransportDetailsSkeleton>
        </TransportDetailsContainer>
        <RowSeparator />
        <FooterRow>
          <Placeholder height="28px" width="107px" />
        </FooterRow>
      </TransportAndBrandedFaresContainer>
    );
  }

  const transportData = adaptTransport({
    transportId: selectedTransport.id,
    inbound: selectedTransport.inbound,
    outbound: selectedTransport.outbound,
  });
  const { locale } = localization;
  const { outbound, inbound } = transportData;

  const outBoundSegments = outbound.segments;
  const outBoundSegmentsLength = outBoundSegments.length - 1;
  const inBoundSegments = inbound.segments;
  const inBoundSegmentsLength = inBoundSegments.length - 1;

  const outboundCompanies = toUniqueCompanies(outBoundSegments);
  const inboundCompanies = toUniqueCompanies(inBoundSegments);
  const arrivalAirportCode = outBoundSegments[outBoundSegmentsLength].arrival.hub.id;
  const debugIsVisible = showDebug;

  const handleOnMouseEnter = (): void => {
    airportMarkerSelection(arrivalAirportCode, true);
  };

  const handleOnMouseLeave = (): void => {
    airportMarkerSelection(arrivalAirportCode, false);
  };

  const differentDepartureAirportCodes =
    outBoundSegments[0].departure.hub.id !== inBoundSegments[inBoundSegmentsLength].arrival.hub.id;

  const differentArrivalAirportCodes =
    outBoundSegments[outBoundSegmentsLength].arrival.hub.id !== inBoundSegments[0].departure.hub.id;

  const outboundProps: EvoTransportProps = {
    isOutBound: true,
    dictionary,
    departureDate: outBoundSegments?.[0]?.departure?.datetime,
    departureAirportCode: outBoundSegments?.[0]?.departure?.hub?.id,
    departureAirportName: outBoundSegments?.[0]?.departure?.hub?.name,
    arrivalDate: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.datetime,
    arrivalAirportCode: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.hub?.id,
    arrivalAirportName: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.hub?.name,
    providerCode: outBoundSegments?.[0]?.transport?.companyCode,
    providerName: outBoundSegments?.[0]?.transport?.companyName,
    numberOfStops: outBoundSegmentsLength,
    durationInMinutes: outbound?.durationInMinutes,
    locale,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    companies: outboundCompanies,
  };

  const inboundProps: EvoTransportProps = {
    isOutBound: false,
    dictionary,
    departureDate: inBoundSegments?.[0]?.departure?.datetime,
    departureAirportCode: inBoundSegments?.[0]?.departure?.hub?.id,
    departureAirportName: inBoundSegments?.[0]?.departure?.hub?.name,
    arrivalDate: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.datetime,
    arrivalAirportCode: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.hub?.id,
    arrivalAirportName: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.hub?.name,
    providerCode: inBoundSegments?.[0]?.transport?.companyCode,
    providerName: inBoundSegments?.[0]?.transport?.companyName,
    numberOfStops: inBoundSegmentsLength,
    durationInMinutes: inbound?.durationInMinutes,
    locale,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    companies: inboundCompanies,
  };

  const { co2Footprint } = selectedTransport;
  const showCo2Badge = !!co2Footprint && co2Footprint.co2kg > 0;

  return (
    <TransportAndBrandedFaresContainer $isTransportNotAvailable={currentTransportNotAvailable}>
      <TransportDetailsContainer
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        $isMobile={isMobile}
      >
        <EvoTransport {...outboundProps} />
        <Separator />
        <EvoTransport {...inboundProps} />
      </TransportDetailsContainer>
      {currentTransportNotAvailable ? (
        <NoTransportAvailableBanner role="alert">
          <NoTransportAvailableIcon />
          <NoTransportAvailableText variant="BodySmall">
            {t(
              isTrain
                ? 'vacation.select.train.message.selected_train_not_available'
                : 'vacation.select.flight.message.selected_flight_not_available'
            )}
          </NoTransportAvailableText>
        </NoTransportAvailableBanner>
      ) : (
        <>
          <RowSeparator />
          <FooterRow>
            {showCo2Badge ? <EvoFootprintBadge footprint={co2Footprint} locale={locale} /> : <span />}
            {onTransportDetailsClick ? (
              <SubtleButton
                onClick={onTransportDetailsClick}
                onKeyUp={keyUpHandler(onTransportDetailsClick)}
                data-testid="show-flight-details"
              >
                <Text
                  variant="Body"
                  css={css`
                    color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
                    text-decoration: underline;
                  `}
                >
                  {isMobile
                    ? t(
                        isTrain ? 'vacation.select.trainmodal.title.mobile' : 'vacation.select.flightmodal.title.mobile'
                      )
                    : t(isTrain ? 'vacation.select.trainmodal.title' : 'vacation.select.flightmodal.title')}
                </Text>
              </SubtleButton>
            ) : null}
          </FooterRow>
          <OvernightBannerWrapper>
            <EvoOvernightBanner
              inbound={selectedTransport.inbound}
              outbound={selectedTransport.outbound}
              onMessageClicked={onTransportDetailsClick}
            />
          </OvernightBannerWrapper>
        </>
      )}
      {debugIsVisible && (
        <DebugInfoContainer style={{ marginTop: '-4px' }}>
          <DebugBlock data={[{ value: transportData.tripId }]} showSeparator={false} />
        </DebugInfoContainer>
      )}
    </TransportAndBrandedFaresContainer>
  );
};

export default EvoTransportDetailsCard;
